import React, { useCallback, useRef, useState } from 'react'

import { AcornsIcon } from '@acorns/icons'
import {
  ErrorState,
  Spinner,
  ToastNotification,
  ToastPosition,
} from '@acorns/web-components'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import { v4 } from 'uuid'

import { trackAiChatSuggestedQuestionButtonTappedPayload } from 'generated/trackingEvents'
import {
  ChatChoiceActionMetadata,
  useAllChatHistoryMessagesQuery,
  useOnChatMessageStreamSubscription,
  useRateChatMessageMutation,
  useRecommendedSearchTermsQuery,
  useSendChatMessageMutation,
  useUserQueryQuery,
} from 'generated/types'
import {
  AI_ASSISTANT_CONTAINER,
  AI_CHAT_INPUT,
  AI_CHAT_SEND_CTA,
} from 'utils/testing/testIDs'

import { MessageList } from './MessageList'
import { RecommendedSearchTerms } from './RecommendedSearchTerms'
import {
  AcornContainer,
  BasicContainer,
  ChatInput,
  ContentContainer,
  GreetingContainer,
  InnerInputContainer,
  InputContainer,
  SendIcon,
  StartConversationContainer,
  StyledAcorn,
  SubmitButton,
  TextContainer,
  ToastContainer,
} from './styled-components'
import { TypingDots } from './TypingDots'
import { AI_CHAT_FEEDBACK_ORIGIN } from './utils/constants'
import { buildDateDivider, getDateKey } from './utils/dateUtils'
import { MessageFooterWidgets, extractWidgets } from './utils/extractWidgets'
import { getGreetingRemoteStringKey } from './utils/getGreetingRemoteStringKey'
import { processChatHistoryMessages } from './utils/messageUtils'
import { Message, ResponseMessage, SentMessage } from './utils/types'

const MAX_INPUT_LENGTH = 1024

export const ChatContent = ({
  hasError,
  onReinitializeSession,
  onCloseAssistant,
  sessionId,
  isMinimized,
}: {
  onCloseAssistant: () => void
  hasError: boolean
  onReinitializeSession: () => void
  sessionId: string | undefined
  isMinimized: boolean
}) => {
  const { getRemoteString } = useRemoteStrings()
  const { track } = useAnalyticsClient()
  const [input, setInput] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    textarea.style.height = 'auto'
    const maxHeight = 120
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`
  }, [])

  const resetTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) {
      return
    }
    textarea.style.height = 'auto'
    textarea.style.overflowY = 'hidden'
  }, [])

  const [messages, setMessages] = useState<Record<string, Message>>({})
  const [messageOrder, setMessageOrder] = useState<string[]>([])
  const [botIsTyping, setBotIsTyping] = useState(false)
  const [waitingForResponse, setWaitingForResponse] = useState(false)

  const [latestDateKey, setLatestDateKey] = useState<string | null>(null)

  const { data: userData, loading: userLoading } = useUserQueryQuery()
  const { data: searchTermsData, loading: searchTermsLoading } =
    useRecommendedSearchTermsQuery()

  const [rateChatMessage] = useRateChatMessageMutation()

  useOnChatMessageStreamSubscription({
    variables: {
      sessionId: sessionId || '',
    },
    skip: !sessionId,
    fetchPolicy: 'no-cache',
    onSubscriptionData: (options) => {
      const newMessage = options.subscriptionData?.data?.onChatMessageStream

      if (
        newMessage?.__typename !== 'ChatHistoryMessageBody' &&
        newMessage?.__typename !== 'ChatHistoryMessageFooter'
      ) {
        return
      }

      const messageId = newMessage.id

      if (newMessage.__typename === 'ChatHistoryMessageBody') {
        setMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages }
          const existingMessage = updatedMessages[messageId]

          if (existingMessage) {
            const currentText = existingMessage.text || ''
            const newText = newMessage.text || ''
            updatedMessages[messageId] = {
              ...existingMessage,
              text: currentText + newText,
            }
          } else {
            updatedMessages[messageId] = {
              messageType: 'ResponseMessage',
              id: messageId,
              text: newMessage.text || '',
              createdAt: new Date().toISOString(),
            }
          }

          return updatedMessages
        })

        setMessageOrder((prevOrder) => {
          if (prevOrder[0] !== messageId) {
            return [messageId, ...prevOrder]
          }
          return prevOrder
        })

        setBotIsTyping(false)
      } else if (newMessage.__typename === 'ChatHistoryMessageFooter') {
        setMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages }
          const existingMessage = updatedMessages[messageId]

          const {
            deeplinks,
            citations,
            customerSupportChatWidgets,
            chatChoices,
          } = extractWidgets(
            newMessage.chatWidgets as Array<MessageFooterWidgets>,
          )

          if (
            existingMessage &&
            existingMessage.messageType === 'ResponseMessage'
          ) {
            updatedMessages[messageId] = {
              ...existingMessage,
              canRate: newMessage.canRate,
              deeplinks,
              customerSupportChatWidgets,
              citations,
              chatChoices,
            }
          } else {
            updatedMessages[messageId] = {
              messageType: 'ResponseMessage',
              id: messageId,
              canRate: newMessage.canRate,
              deeplinks,
              customerSupportChatWidgets,
              citations,
              text: existingMessage?.text || '',
              createdAt: existingMessage?.createdAt || new Date().toISOString(),
            }
          }

          return updatedMessages
        })
        setWaitingForResponse(false)
      }
    },
  })

  const addMessage = (uuid: string, message: Message) => {
    const newMessage = { [uuid]: message }
    setMessages((prevMessages) => ({
      ...newMessage,
      ...prevMessages,
    }))

    const dateKey = getDateKey(message.createdAt)

    if (dateKey !== latestDateKey) {
      setLatestDateKey(dateKey)
      setMessageOrder([uuid, dateKey, ...messageOrder])
      setMessages((prevMessages) => ({
        [dateKey]: buildDateDivider(message.createdAt, getRemoteString),
        ...prevMessages,
      }))
    } else {
      setMessageOrder([uuid, ...messageOrder])
    }
  }

  const [sendChatMessage] = useSendChatMessageMutation({
    onCompleted(response) {
      if (
        response.sendChatMessage.__typename === 'ChatSessionNotFound' ||
        response.sendChatMessage.__typename === 'ChatUnavailableError'
      ) {
        handleError()
      }
    },
    onError() {
      handleError()
    },
  })

  const { data: chatHistoryData, loading: chatHistoryLoading } =
    useAllChatHistoryMessagesQuery({
      variables: {
        pagination: {
          limit: 100,
        },
      },
      fetchPolicy: 'no-cache',
    })

  const handleChatHistoryClick = () => {
    if (!chatHistoryData?.allChatHistoryMessages?.edges?.length) {
      return
    }

    track(
      ...trackAiChatSuggestedQuestionButtonTappedPayload({
        origin: AI_CHAT_FEEDBACK_ORIGIN,
        ctaTitle: getRemoteString({
          key: 'ai_assistant.chat.search_terms.chat_history',
        }),
      }),
    )

    const uuid = v4()
    const sentMessage: SentMessage = {
      messageType: 'SentMessage',
      text: getRemoteString({
        key: 'ai_assistant.chat.search_terms.chat_history',
      }),
      id: uuid,
      createdAt: new Date().toISOString(),
    }
    addMessage(uuid, sentMessage)
    setBotIsTyping(true)
    setWaitingForResponse(true)

    const { messageMap, messageOrder, mostRecentMessageDateKey } =
      processChatHistoryMessages({
        response: chatHistoryData,
        getRemoteString,
      })

    setMessages(messageMap)
    setMessageOrder(messageOrder)
    setLatestDateKey(mostRecentMessageDateKey)
    setBotIsTyping(false)
    setWaitingForResponse(false)
  }

  const handleError = () => {
    const uuid = v4()
    setBotIsTyping(false)
    setWaitingForResponse(false)
    const responseMessage = {
      messageType: 'ResponseMessage',
      text: getRemoteString({
        key: 'ai_assistant.chat.error_message',
      }),
      isError: true,
      id: uuid,
      createdAt: new Date().toISOString(),
    } as ResponseMessage
    addMessage(uuid, responseMessage)
  }

  const callSendChatMessageMutation = (
    searchTerm: string,
    metadata?: Omit<ChatChoiceActionMetadata, '__typename'>,
  ) => {
    if (waitingForResponse) {
      return
    }
    const uuid = v4()
    const sentMessage: SentMessage = {
      messageType: 'SentMessage',
      text: searchTerm,
      id: uuid,
      createdAt: new Date().toISOString(),
    }
    addMessage(uuid, sentMessage)
    setBotIsTyping(true)
    setWaitingForResponse(true)
    if (sessionId) {
      sendChatMessage({
        variables: {
          message: searchTerm,
          sessionId,
          metadata,
        },
      })
    }
  }

  const handleRateResponse = ({
    messageId,
    rating,
  }: {
    messageId: string
    rating?: number
  }) => {
    const currentMessages = Object.assign({}, messages)
    currentMessages[messageId] = {
      ...currentMessages[messageId],
      rating,
    } as ResponseMessage
    setMessages(currentMessages)

    rateChatMessage({
      variables: {
        messageId,
        ...(rating ? { rating } : {}),
      },
    })
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!waitingForResponse && input.trim().length > 0) {
      callSendChatMessageMutation(input.trim())
      setInput('')
      resetTextareaHeight()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (!waitingForResponse && input.trim().length > 0) {
        callSendChatMessageMutation(input.trim())
        setInput('')
        resetTextareaHeight()
      }
    }
  }

  const handleClickRecommendedSearchTerm = (searchTerm: string) => () => {
    track(
      ...trackAiChatSuggestedQuestionButtonTappedPayload({
        origin: AI_CHAT_FEEDBACK_ORIGIN,
        ctaTitle: searchTerm,
      }),
    )
    callSendChatMessageMutation(searchTerm)
  }

  const firstName = userData?.user?.firstName || ''
  const hasSearchTerms = Boolean(
    searchTermsData?.recommendedSearchTerms?.length ||
    chatHistoryData?.allChatHistoryMessages?.edges?.length,
  )

  if (userLoading || searchTermsLoading || chatHistoryLoading) {
    return (
      <BasicContainer isMinimized={isMinimized}>
        <Spinner />
      </BasicContainer>
    )
  }

  if (hasError) {
    return (
      <BasicContainer isMinimized={isMinimized}>
        <ErrorState
          darkMode={false}
          errorMessage={getRemoteString({
            key: 'error.retry.title',
          })}
          errorSubMessage={getRemoteString({
            key: 'error.retry.subtitle',
          })}
          onClickReload={onReinitializeSession}
        />
      </BasicContainer>
    )
  }

  return (
    <>
      <ContentContainer
        data-testid={AI_ASSISTANT_CONTAINER}
        isMinimized={isMinimized}
      >
        <ToastContainer>
          <ToastNotification
            position={ToastPosition.topCenter}
            shouldDismiss={true}
          />
        </ToastContainer>
        {botIsTyping ? <TypingDots /> : null}
        {messageOrder.length ? (
          <MessageList
            messageOrder={messageOrder}
            messages={messages}
            onRateResponse={handleRateResponse}
            onCloseAssistant={onCloseAssistant}
            sessionId={sessionId || ''}
            onSendMessage={callSendChatMessageMutation}
            isTyping={botIsTyping}
          />
        ) : (
          <StartConversationContainer>
            <GreetingContainer hasSearchTerms={hasSearchTerms}>
              <AcornContainer hasSearchTerms={hasSearchTerms}>
                <StyledAcorn
                  icon={AcornsIcon.Icon.TransactionAcornsFv}
                  width={27}
                  height={27}
                />
              </AcornContainer>
              <TextContainer hasSearchTerms={hasSearchTerms}>
                {getRemoteString({
                  key: getGreetingRemoteStringKey(),
                  variables: [firstName],
                })}
              </TextContainer>
            </GreetingContainer>
            <RecommendedSearchTerms
              hasChatHistory={Boolean(
                chatHistoryData?.allChatHistoryMessages?.edges?.length,
              )}
              searchTerms={searchTermsData?.recommendedSearchTerms || []}
              onClickRecommendedSearchTerm={handleClickRecommendedSearchTerm}
              onClickChatHistory={handleChatHistoryClick}
            />
          </StartConversationContainer>
        )}
      </ContentContainer>
      <InputContainer>
        <form onSubmit={handleSubmit}>
          <InnerInputContainer>
            <ChatInput
              ref={textareaRef}
              rows={1}
              data-testid={AI_CHAT_INPUT}
              value={input}
              placeholder={getRemoteString({
                key: 'acorns_intelligence.search_bar.title',
              })}
              onChange={(e) => {
                const newValue = e.target.value
                if (newValue.length <= MAX_INPUT_LENGTH) {
                  setInput(newValue)
                } else {
                  setInput(newValue.slice(0, MAX_INPUT_LENGTH))
                }
                adjustTextareaHeight()
              }}
              onKeyDown={handleKeyDown}
            />
            <SubmitButton
              type="submit"
              disabled={waitingForResponse || input.trim().length === 0}
              data-testid={AI_CHAT_SEND_CTA}
            >
              <SendIcon icon={AcornsIcon.Icon.UtilityArrowRight} />
            </SubmitButton>
          </InnerInputContainer>
        </form>
      </InputContainer>
    </>
  )
}
